import { useTheme } from "next-themes"
import { useState, useEffect } from "react"
import { useBrandConfig } from "@/lib/brand-context"
import { cn } from "@/lib/utils"
import { ShelfiLogo } from "./ShelfiLogo"

export type BrandLogoProps = {
  variant?: "primary" | "motion" | "icon"
  size?: "sm" | "md" | "lg" | "xl"
  animated?: boolean
  className?: string
}

const sizes = {
  sm: { width: 120, height: 30 },
  md: { width: 200, height: 50 },
  lg: { width: 300, height: 75 },
  xl: { width: 400, height: 100 },
}

export function BrandLogo({
  variant = "primary",
  size = "md",
  animated = false,
  className,
}: BrandLogoProps) {
  const { resolvedTheme } = useTheme()
  const [mounted, setMounted] = useState(false)
  const [imgError, setImgError] = useState(false)
  // Use brand config from context to ensure consistency between server and client
  const brandConfig = useBrandConfig()

  useEffect(() => {
    setMounted(true)
  }, [])

  const { width, height } = sizes[size]

  const hasCustomLogo = brandConfig.logoLightUrl !== null || brandConfig.logoDarkUrl !== null

  // If no custom logo is configured, render the default ShelfiLogo
  if (!hasCustomLogo) {
    return <ShelfiLogo variant={variant} size={size} animated={animated} className={className} />
  }

  // Before mount, render a placeholder with the same dimensions to avoid layout shift
  if (!mounted) {
    return (
      <div style={{ width, height }} className={cn("inline-block", className)} aria-hidden="true" />
    )
  }

  // If image failed to load, fall back to brand name as text
  if (imgError) {
    return (
      <div
        style={{ width, height }}
        className={cn("inline-flex items-center justify-center font-bold text-lg", className)}
        role="img"
        aria-label={brandConfig.name}
      >
        {brandConfig.name}
      </div>
    )
  }

  // Select the appropriate logo URL based on the current theme
  const isDark = resolvedTheme === "dark"
  const logoUrl = isDark
    ? (brandConfig.logoDarkUrl ?? brandConfig.logoLightUrl)
    : (brandConfig.logoLightUrl ?? brandConfig.logoDarkUrl)

  // If somehow both URLs are null (shouldn't happen given hasCustomLogo check),
  // fall back to ShelfiLogo
  if (!logoUrl) {
    return <ShelfiLogo variant={variant} size={size} animated={animated} className={className} />
  }

  return (
    <img
      src={logoUrl}
      alt={brandConfig.name}
      width={width}
      height={height}
      loading="eager"
      onError={() => setImgError(true)}
      className={cn("object-contain", className)}
    />
  )
}
